import axios from "axios";

/**
 * Client for the `kensrin_server` (Node) reporting API.
 *
 * The Reports/Trips views were built against the legacy PHP `/vehicle_status`
 * payload, which returns *positional arrays* for trips and stoppages
 * (e.g. `drive[4]`, `drive[15].from`, `stop[13]`). Instead of rewriting those
 * templates (and their PDF export refs) the Node responses are adapted back
 * into that shape here, so the views can switch with a one-line change and fall
 * back to PHP when the Node API is unavailable.
 */

const NODE_API_URL = process.env.VUE_APP_NODE_API_URL || "";

// IMPORTANT: NODE_API_URL already ends in `/api` (e.g. https://host/api), so
// every path below is written WITHOUT that prefix. Writing "/api/trips" here
// resolves to .../api/api/trips, which 404s - that is what silently emptied the
// reports dashboard while the Trips page kept working.

export const nodeApi = axios.create({
  baseURL: NODE_API_URL,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

/** Node reports are used only when a base URL is configured and not disabled. */
export function nodeReportsEnabled() {
  if (!NODE_API_URL) return false;
  return (
    String(process.env.VUE_APP_USE_NODE_REPORTS || "true").toLowerCase() !==
    "false"
  );
}

/**
 * Session token issued by /login (stored by vuex-persist in localStorage).
 * The summary endpoints require it, and the V1-compatible endpoints expect it in
 * the body.
 */
function authToken() {
  try {
    const raw = localStorage.getItem("vuex");
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return (parsed && parsed.session && parsed.session.token) || undefined;
  } catch (error) {
    return undefined;
  }
}

/* ── formatting helpers ─────────────────────────────────────────────────── */

function toDateKey(value) {
  return String(value || "").slice(0, 10);
}

function pad(value) {
  return String(value).padStart(2, "0");
}

/** Shift a stored UTC datetime into the device's local wall clock. */
function localDateTime(value, tzMinutes) {
  if (!value) return "";
  const parsed = new Date(String(value).replace(" ", "T") + "Z");
  if (isNaN(parsed.getTime())) return String(value);
  const shifted = new Date(parsed.getTime() + (Number(tzMinutes) || 0) * 60000);
  return (
    shifted.getUTCFullYear() +
    "-" +
    pad(shifted.getUTCMonth() + 1) +
    "-" +
    pad(shifted.getUTCDate()) +
    " " +
    pad(shifted.getUTCHours()) +
    ":" +
    pad(shifted.getUTCMinutes())
  );
}

function formatDuration(totalSeconds) {
  const secs = Math.max(Math.round(Number(totalSeconds) || 0), 0);
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  if (hours > 0) return hours + "h " + minutes + "m";
  if (minutes > 0) return minutes + "m";
  return secs + "s";
}

function round(value, decimals) {
  const factor = Math.pow(10, decimals === undefined ? 2 : decimals);
  return Math.round((Number(value) || 0) * factor) / factor;
}

/**
 * The summary service does not geocode, so address columns are filled with the
 * coordinates rather than left blank.
 */
function coordLabel(lat, lng) {
  const a = Number(lat);
  const b = Number(lng);
  if (!isFinite(a) || !isFinite(b)) return "";
  return a.toFixed(5) + ", " + b.toFixed(5);
}

/* ── adapters: Node rows -> legacy positional arrays ────────────────────── */

function toLegacyDrive(trip, tzMinutes, kmpl) {
  const drive = [];
  drive[2] = Number(trip.start_lat);
  drive[3] = Number(trip.start_lng);
  drive[4] = localDateTime(trip.trip_start, tzMinutes);
  drive[5] = localDateTime(trip.trip_end, tzMinutes);
  drive[6] = formatDuration(trip.duration_sec);
  drive[7] = round(trip.distance_km, 2);
  drive[8] = Math.round(Number(trip.max_speed_kmh) || 0);
  drive[9] = Math.round(Number(trip.avg_speed_kmh) || 0);
  // Fuel used for this trip, from the asset's own rate (gs_objects.fcr, km per
  // litre). 0 means the asset has no consumption rate configured.
  drive[10] = kmpl > 0 ? round((Number(trip.distance_km) || 0) / kmpl, 2) : 0;
  drive[11] = Number(trip.point_count) || 0;
  // Prefer the geocoded address (gs_trip_summary.start_address/end_address,
  // written by the address worker); fall back to coordinates while it is empty.
  const startLabel = trip.start_address || coordLabel(trip.start_lat, trip.start_lng);
  const endLabel = trip.end_address || coordLabel(trip.end_lat, trip.end_lng);
  drive[13] = startLabel;
  drive[15] = {
    from: startLabel,
    to: endLabel,
  };
  return drive;
}

function toLegacyStop(stop, tzMinutes) {
  const row = [];
  row[2] = Number(stop.lat);
  row[3] = Number(stop.lng);
  row[6] = localDateTime(stop.stop_start, tzMinutes);
  row[7] = stop.stop_end ? localDateTime(stop.stop_end, tzMinutes) : "";
  row[8] = formatDuration(stop.duration_sec);
  // Server reverse-geocoded address, falling back to the coordinates.
  row[13] = stop.address || coordLabel(stop.lat, stop.lng);
  return row;
}

/** Combine `/api/trips` + `/api/stops` into the legacy report object. */
export function buildLegacyTripReport(tripsResponse, stopsResponse) {
  const tzMinutes =
    (tripsResponse && tripsResponse.timezoneMinutes) ||
    (stopsResponse && stopsResponse.timezoneMinutes) ||
    0;

  const trips =
    tripsResponse && Array.isArray(tripsResponse.data) ? tripsResponse.data : [];
  const stops =
    stopsResponse && Array.isArray(stopsResponse.data) ? stopsResponse.data : [];

  // The asset's own consumption rate (km per litre), supplied by /api/trips from
  // gs_objects.fcr. 0 when the asset has none configured.
  const kmpl = Number(tripsResponse && tripsResponse.kmpl) || 0;

  let driveSecs = 0;
  let distance = 0;
  let topSpeed = 0;
  trips.forEach(function (trip) {
    driveSecs += Number(trip.duration_sec) || 0;
    distance += Number(trip.distance_km) || 0;
    topSpeed = Math.max(topSpeed, Number(trip.max_speed_kmh) || 0);
  });

  let stopSecs = 0;
  stops.forEach(function (stop) {
    stopSecs += Number(stop.duration_sec) || 0;
  });

  return {
    drives: trips.map(function (trip) {
      return toLegacyDrive(trip, tzMinutes, kmpl);
    }),
    stops: stops.map(function (stop) {
      return toLegacyStop(stop, tzMinutes);
    }),
    // The legacy report shape always carries an `events` array (the views read
    // `trip_data.events.length`). The Node summaries have no event rows, so this
    // stays empty — but it must exist or those renders throw.
    events: [],
    drives_duration: formatDuration(driveSecs),
    stops_duration: formatDuration(stopSecs),
    route_length: round(distance, 2),
    // Fuel used = distance / the asset's km-per-litre rate (0 when unconfigured).
    fuel_consumption: kmpl > 0 ? round(distance / kmpl, 2) : 0,
    kmpl: kmpl,
    avg_speed: driveSecs > 0 ? Math.round(distance / (driveSecs / 3600)) : 0,
    top_speed: Math.round(topSpeed),
    meta: {
      source: "kensrin_server",
      timezoneMinutes: tzMinutes,
      trips: (tripsResponse && tripsResponse.meta) || null,
      stops: (stopsResponse && stopsResponse.meta) || null,
    },
  };
}

/* ── public API ─────────────────────────────────────────────────────────── */

function describeError(error) {
  if (error && error.response && error.response.status) {
    return "HTTP " + error.response.status;
  }
  return (error && error.message) || "request failed";
}

/**
 * Trip + stoppage report for one asset, in the legacy shape.
 * Resolves to `{ status: 200, data }` or `{ status: 500, state: 500 }` so the
 * calling view can fall back to the PHP endpoint on a non-200 status.
 */
export async function fetchTripReport(params) {
  const imei = params.imei;
  let from = toDateKey(params.from);
  let to = toDateKey(params.to);

  if (!imei || !from || !to) {
    return { status: 400, state: 400, payload: "imei, from and to are required" };
  }
  if (from > to) {
    const swap = from;
    from = to;
    to = swap;
  }

  const query = { imei: imei, from: from, to: to, token: authToken() };
  if (params.tz !== undefined && params.tz !== null) query.tz = params.tz;

  try {
    const responses = await Promise.all([
      nodeApi.get("/trips", { params: query }),
      nodeApi.get("/stops", { params: query }),
    ]);
    const report = buildLegacyTripReport(responses[0].data, responses[1].data);
    return { status: 200, data: report, pending: reportPending(report) };
  } catch (error) {
    return { status: 500, state: 500, payload: describeError(error) };
  }
}

/** Validate/normalise a `{ imei, from, to }` range (swaps reversed dates). */
function normalizeRange(params) {
  const imei = params.imei;
  let from = toDateKey(params.from);
  let to = toDateKey(params.to);
  if (from && to && from > to) {
    const swap = from;
    from = to;
    to = swap;
  }
  return { imei: imei, from: from, to: to };
}

function rangeQuery(normalized, tz) {
  const query = {
    imei: normalized.imei,
    from: normalized.from,
    to: normalized.to,
    token: authToken(),
  };
  if (tz !== undefined && tz !== null) query.tz = tz;
  return query;
}

/**
 * Trips only — for a report tab that needs just the movement rows.
 * Same legacy shape as `fetchTripReport` (drives/totals), no `/api/stops` call.
 */
export async function fetchTrips(params) {
  const range = normalizeRange(params);
  if (!range.imei || !range.from || !range.to) {
    return { status: 400, state: 400, payload: "imei, from and to are required" };
  }
  try {
    const response = await nodeApi.get("/trips", {
      params: rangeQuery(range, params.tz),
    });
    const report = buildLegacyTripReport(response.data, null);
    return { status: 200, data: report, pending: reportPending(report) };
  } catch (error) {
    return { status: 500, state: 500, payload: describeError(error) };
  }
}

/**
 * Stops only — for a report tab that needs just the stoppage rows.
 * No `/api/trips` call, so the movement pipeline is never touched.
 */
export async function fetchStops(params) {
  const range = normalizeRange(params);
  if (!range.imei || !range.from || !range.to) {
    return { status: 400, state: 400, payload: "imei, from and to are required" };
  }
  try {
    const response = await nodeApi.get("/stops", {
      params: rangeQuery(range, params.tz),
    });
    const report = buildLegacyTripReport(null, response.data);
    return { status: 200, data: report, pending: reportPending(report) };
  } catch (error) {
    return { status: 500, state: 500, payload: describeError(error) };
  }
}


/** How often to re-ask while the summaries are still being built. */
const SUMMARY_POLL_MS = Number(process.env.VUE_APP_SUMMARY_POLL_MS || 15000);
const SUMMARY_POLL_ATTEMPTS = Number(process.env.VUE_APP_SUMMARY_POLL_ATTEMPTS || 40);

/**
 * Are the summaries this report was built from still catching up?
 *
 * `true`  - the API reports a lag, keep asking.
 * `false` - the API reports it is current, stop asking.
 * `null`  - the API does not report freshness (older server), keep asking.
 */
function reportPending(report) {
  const meta = report && report.meta;
  const freshness = [meta && meta.trips, meta && meta.stops]
    .map(function (entry) {
      return entry && entry.freshness;
    })
    .filter(Boolean);
  if (!freshness.length) return null;
  return freshness.some(function (entry) {
    return entry.pending === true;
  });
}

/**
 * Keeps re-asking for a report until the summaries behind it are current.
 *
 * The summaries are produced by the aggregator worker, so the first reply can
 * legitimately be behind. `onUpdate(data, info)` receives every newer report so
 * the view can refresh in place, and the returned function cancels polling
 * (call it from `beforeDestroy`).
 */
function createReportWatcher(fetchFn, params, onUpdate, options) {
  const opts = options || {};
  const pollMs = Number(opts.pollMs || SUMMARY_POLL_MS);
  const maxAttempts = Number(opts.maxAttempts || SUMMARY_POLL_ATTEMPTS);
  let attempts = 0;
  let cancelled = false;
  let timer = null;

  async function tick() {
    if (cancelled) return;
    attempts += 1;

    const res = await fetchFn(params);
    if (cancelled) return;

    if (res.status === 200) {
      onUpdate(res.data, { attempt: attempts, pending: res.pending });
      if (res.pending === false) return;
    }

    if (attempts >= maxAttempts) return;
    timer = setTimeout(tick, pollMs);
  }

  timer = setTimeout(tick, pollMs);

  return function cancel() {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
}

/** Trip + stoppage poller (both endpoints). */
export function watchTripReport(params, onUpdate, options) {
  return createReportWatcher(fetchTripReport, params, onUpdate, options);
}

/** Trips-only poller — used by tabbed reports that load one dataset on demand. */
export function watchTripsReport(params, onUpdate, options) {
  return createReportWatcher(fetchTrips, params, onUpdate, options);
}

/** Stops-only poller — used by tabbed reports that load one dataset on demand. */
export function watchStopsReport(params, onUpdate, options) {
  return createReportWatcher(fetchStops, params, onUpdate, options);
}

/**
 * Fleet-wide daily summary (one request, all assets).
 *   fetchFleetDailySummary({ date: "2026-09-27" })
 *   fetchFleetDailySummary({ from: "2026-09-01", to: "2026-09-07", managerId: 31 })
 */
export async function fetchFleetDailySummary(params) {
  const query = { token: authToken() };
  if (params.date) {
    query.date = toDateKey(params.date);
  } else {
    query.from = toDateKey(params.from);
    query.to = toDateKey(params.to);
  }
  if (params.managerId !== undefined) query.managerId = params.managerId;
  if (params.imeis && params.imeis.length) query.imei = params.imeis.join(",");
  if (params.includeEmpty) query.includeEmpty = "true";
  if (params.compute) query.compute = "true";
  if (params.maxCompute !== undefined) query.maxCompute = params.maxCompute;
  if (params.limit !== undefined) query.limit = params.limit;

  const response = await nodeApi.get("/summaries/daily/fleet", {
    params: query,
  });
  return { status: 200, data: response.data.data, meta: response.data.meta };
}

export async function fetchDailySummary(params) {
  const query = {
    imei: params.imei,
    from: toDateKey(params.from),
    to: toDateKey(params.to),
    token: authToken(),
  };
  if (params.tz !== undefined) query.tz = params.tz;

  const response = await nodeApi.get("/summaries/daily", { params: query });
  return { status: 200, data: response.data.data, meta: response.data.meta };
}

/* ── V1-compatible endpoints (kensrin_server only, no PHP fallback) ─────── */

function failure(error) {
  return { status: 500, state: 500, payload: describeError(error) };
}

/**
 * POST /get_route — raw route points. Returns `{ status, data }` where data is
 * the legacy array-of-arrays: [dt_tracker, lat, lng, altitude, angle, speed,
 * params, location].
 */
export async function fetchRoutePoints(params) {
  try {
    const response = await nodeApi.post("/get_route", {
      token: authToken(),
      imei: params.imei,
      dtf: toDateTime(params.dtf ?? params.from),
      dtt: toDateTime(params.dtt ?? params.to),
    });
    return { status: 200, data: response.data };
  } catch (error) {
    return failure(error);
  }
}

/**
 * POST /playback — adapts `{dt_tracker, speed, lat, lng, type, angle}` to the
 * `ts`-keyed point shape the play-back view uses.
 */
export async function fetchPlayback(params) {
  try {
    const response = await nodeApi.post("/playback", {
      token: authToken(),
      imei: params.imei,
      date_from: toDateKey(params.dateFrom),
      time_from: params.timeFrom,
      date_to: toDateKey(params.dateTo),
      time_to: params.timeTo,
    });

    const points = (response.data || []).map(function (row) {
      return {
        ts: String(row.dt_tracker || "").replace(" ", "T"),
        dt_tracker: row.dt_tracker,
        lat: Number(row.lat),
        lng: Number(row.lng),
        speed: Number(row.speed) || 0,
        angle: Number(row.angle) || 0,
        type: row.type || "",
      };
    });

    return { status: 200, data: points };
  } catch (error) {
    return failure(error);
  }
}

/** POST /get_event_detail — raw detail object (trail, speed/time history). */
export async function fetchEventDetail(eventId) {
  try {
    const response = await nodeApi.post("/get_event_detail", {
      token: authToken(),
      id: eventId,
    });
    return { status: 200, data: response.data };
  } catch (error) {
    return failure(error);
  }
}

/**
 * POST /api/get_alerts — the alerts/violations rows for one asset over a date
 * range, reverse-geocoded to street addresses by the server.
 *
 * `source: "report"` keeps the full row cap (up to 1000) and `geocode: true`
 * asks the server to fill the address column from its geocoder cache. The view
 * is expected to keep the range small (<= 2 days), which the server also
 * enforces so a report can't fan out into thousands of geocoder lookups.
 */
export async function fetchAlerts(params) {
  const payload = {
    token: authToken(),
    source: "report",
    imei: params.imei,
    dtf: toDateTime(params.dtf !== undefined ? params.dtf : params.from),
    dtt: toDateTime(params.dtt !== undefined ? params.dtt : params.to, true),
  };
  let lastError;
  // Ask the server to reverse-geocode the rows, but fall back to a plain fetch:
  // geocoding is best-effort, and a failure there must NOT blank the alerts
  // report (an empty list is worse than one showing raw coordinates).
  for (const geocode of [true, false]) {
    try {
      const response = await nodeApi.post("/get_alerts", { ...payload, geocode });
      if (Array.isArray(response.data)) return { status: 200, data: response.data };
      lastError = new Error("Unexpected alerts response");
    } catch (error) {
      lastError = error;
    }
  }
  return failure(lastError);
}

/**
 * Queue a large-range report to be generated in the background and emailed.
 * The server stores the job; a worker produces a CSV (trips + stops + alerts)
 * and mails it, so the request returns immediately.
 */
export async function requestBackgroundReport(params) {
  try {
    const response = await nodeApi.post("/reports/request", {
      token: authToken(),
      imei: params.imei,
      from: toDateKey(params.from),
      to: toDateKey(params.to),
      email: params.email,
    });
    return { status: response.status, data: response.data };
  } catch (error) {
    const body = error && error.response && error.response.data;
    return {
      status: (error && error.response && error.response.status) || 500,
      payload: (body && body.message) || describeError(error),
      data: body,
    };
  }
}

/** Poll the status of a background report job (queued/processing/done/error). */
export async function fetchReportJobStatus(id) {
  try {
    const response = await nodeApi.get("/reports/status", {
      params: { token: authToken(), id },
    });
    return { status: 200, data: response.data && response.data.data };
  } catch (error) {
    return failure(error);
  }
}

/** Keep the time component when the view supplies a full datetime. */
function toDateTime(value, endOfDay) {
  const text = String(value || "").trim();
  if (text.length <= 10) return text + (endOfDay ? " 23:59:59" : " 00:00:00");
  return text;
}
